import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  ArrowLeft,
  Pencil,
  Type,
  Eye,
  ArrowDownToLine,
  Hand,
  ListOrdered,
  Lock,
  CalendarClock,
  StickyNote,
  FolderOpen,
  Layers,
  Headphones,
  MoveUpRight,
  Hash,
  Calculator,
  Wand2,
  Smartphone,
  KeyRound,
  Minimize2,
  RotateCcw,
  Repeat,
} from "lucide-react";
import { BrandWordmark } from "@/components/Brand";

/**
 * Tutorial page — a written walkthrough structured around the four entry
 * points of the fake Notes folder. Lives at `/tutorial`. Magician-facing
 * only.
 */
export default function Tutorial() {
  const navigate = useNavigate();

  return (
    <div
      className="min-h-screen w-full px-5 py-8 sm:py-12 mr-luxe mr-grain"
      style={{
        background:
          "radial-gradient(900px 500px at 85% 5%, rgba(220,180,60,0.10), transparent 60%), radial-gradient(1100px 700px at 15% 105%, rgba(180,28,40,0.48), transparent 55%), radial-gradient(800px 500px at 50% 50%, rgba(140,20,30,0.18), transparent 70%), #0a0306",
      }}
      data-testid="tutorial-page"
    >
      <div className="mr-dust" aria-hidden="true" />
      <div className="max-w-2xl mx-auto pb-16">
        <div className="flex items-center justify-between mb-10 mr-rise">
          <Button
            variant="ghost"
            onClick={() => navigate("/")}
            className="text-white/70 hover:text-white hover:bg-white/5 -ml-2"
            data-testid="tutorial-back"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div className="text-[10px] tracking-[0.42em] uppercase text-white/40">
            Tutorial
          </div>
          <div className="w-16" />
        </div>

        <header className="mb-10 flex flex-col items-center text-center mr-rise delay-1">
          <BrandWordmark size="lg" tagline="The Manual" aura />
          <div className="mt-5 mr-rule" style={{ width: "3rem" }} />
          <p className="mt-6 text-[14px] text-white/55 max-w-md leading-relaxed">
            One folder. Four entry points. Each one fires a different
            effect — and your spectator only ever sees a Notes app.
          </p>
        </header>

        {/* THE BASICS */}
        <Section title="The basics" delay={2}>
          <Item icon={Lock} title="The gate">
            On launch the app asks for your personal password. This keeps
            casual onlookers out of the workshop. Once authenticated, the
            device remembers you until you clear app data.
          </Item>
          <Item icon={FolderOpen} title="The decoy folder">
            <span className="text-white/85">/notes</span> looks like an
            ordinary Apple Notes folder filled with chores, ideas and
            grocery lists. Hand the device over from this screen — your
            spectator will never know they are inside a magic app.
          </Item>
          <Item icon={StickyNote} title="Effect routing — one entry, one effect">
            Each row (and the +&nbsp;button) carries its own trick. The
            spectator sees one Notes folder; you see four levers. Settings
            shows you which row is wired to what.
          </Item>
        </Section>

        {/* TOP ROW — SWIPER */}
        <Section title="Top row · Swiper prediction" delay={3}>
          <Effect>
            <p>
              <strong className="text-white/90">Premise:</strong> &ldquo;I
              had a random thought earlier today &mdash; let&rsquo;s see
              if it matters.&rdquo;
            </p>
            <p>
              The spectator freely names, picks or whispers any playing
              card. While they decide, you secretly transmit their card
              into your <strong className="text-white/90">top decoy
              note</strong> using the Swiper gesture &mdash; nothing
              touches the screen visibly. You then open that top note and
              reveal you wrote their card down{" "}
              <em>before they chose</em>. Clean prediction, zero outs.
            </p>
          </Effect>
          <Item icon={Layers} title="Cards mode — any playing card via 3 flicks">
            Turn <strong className="text-white/80">Swiper</strong> on
            (Settings → Swiper) and pick <em>Playing cards</em>. The first
            two flicks pick the rank, the third picks the suit. Picture a
            clock face — up, down, left, right, no diagonals.
          </Item>
          <div
            className="mt-2 p-3 rounded-lg text-[12px] leading-relaxed font-mono whitespace-pre-wrap"
            style={{ background: "rgba(0,0,0,0.32)", color: "rgba(214,201,140,0.85)" }}
          >
{`Value (first two flicks)
  ↑↑  Q    →↑  2    ↓→  5    ←↓  8
  ↑→  A    →→  3    ↓↓  6    ←←  9
  ↑←  J    →↓  4    ↓←  7    ←↑  10
  ↑↓  K    →←  Joker (no suit)

Suit (third flick)
  ↑ ♠ Spades    → ♥ Hearts    ↓ ♣ Clubs    ← ♦ Diamonds`}
          </div>
          <p
            className="mt-2 text-[12px] leading-relaxed italic"
            style={{ color: "rgba(214,201,140,0.7)" }}
            data-testid="clock-method-credit"
          >
            The clock-face method for memorising swipe patterns was
            originally devised by{" "}
            <strong className="not-italic" style={{ color: "rgba(214,201,140,0.95)" }}>
              Paul Longhurst
            </strong>
            .
          </p>
          <Item icon={MoveUpRight} title="Custom mode — your own combos, your own words">
            Switch the mode to <em>Custom words</em> and build any
            sequence of 1, 2 or 3 arrows mapped to a word. The folder
            list watches for your sequence; when it matches, the word is
            queued. Examples:{" "}
            <span className="text-white/85">↑ → &quot;truth&quot;</span>,{" "}
            <span className="text-white/85">↑↑ → &quot;love&quot;</span>,{" "}
            <span className="text-white/85">↑→↓ → &quot;Aether&quot;</span>.
            Sequences with no defined match are silently dropped.
          </Item>
          <Item icon={StickyNote} title="Tap the TOP row to reveal">
            The top decoy row is your prediction note. When you tap it,
            the captured word/card is already in the body — no animation,
            no typewriter — so it reads as written ages ago. Set its
            preview in Settings so the folder line teases the reveal.
          </Item>
          <p className="text-[12.5px] text-white/55 mt-2">
            Mess up a flick? Pause briefly (≈0.7&nbsp;s) and the buffer
            clears itself. Triple-tap <em>Folders</em> to exit altogether.
          </p>
        </Section>

        {/* SECOND ROW — TOTAL RECALL */}
        <Section title="Second row · Total Recall" delay={3}>
          <Effect>
            <p>
              <strong className="text-white/90">Premise:</strong> the
              spectator thinks of a secret <em>master word</em> in a
              category you can name (movie, city, animal &hellip;). You
              never see it. You hand them the phone and ask them to type
              a randomly-feeling list of three words on lines{" "}
              <span className="text-white/85">1.</span>{" "}
              <span className="text-white/85">2.</span>{" "}
              <span className="text-white/85">3.</span> &mdash; in any
              order:
            </p>
            <p className="pl-3 border-l border-[#d6c98c]/40 text-white/65">
              &bull; on any line, a word from the <em>same category</em>{" "}
              as their master word.<br />
              &bull; on any other line, a word starting with the{" "}
              <em>first letter</em> of their master word.<br />
              &bull; on the last line, a word starting with the{" "}
              <em>second letter</em>.
            </p>
            <p>
              You take the phone back and, without hesitation, name their
              master word. The order they typed in &mdash; not the
              visible top-to-bottom order &mdash; gives you the category
              hint and the first two letters.
            </p>
          </Effect>
          <Item icon={Wand2} title="Three numbered lines, revealed in chronological order">
            Open the second decoy row, type{" "}
            <span className="text-white/85">1.</span>{" "}
            <span className="text-white/85">2.</span>{" "}
            <span className="text-white/85">3.</span>{" "}
            on three separate lines and hand the phone over. The spectator
            writes <em>one word per line</em> — in any order, on any line
            — and hands it back.
          </Item>
          <Item icon={Hand} title="Tap ••• to read them in writing order">
            The reveal shows the words in the chronological order the
            spectator wrote them, not the visual top-to-bottom order.
            Whichever line they touched first is your <em>(a)</em>,
            second is <em>(b)</em>, third is <em>(c)</em>.
          </Item>
          <Item icon={Smartphone} title="First touch wins">
            Edits and backspaces never change the order — once a line
            has received its first non-empty character it&rsquo;s locked.
            If the spectator only fills two lines, only those two land
            in the reveal.
          </Item>
          <p className="text-[12.5px] text-white/55 mt-2">
            Reveal styles (peek / scroll / whisper) and the template
            <span className="text-white/75"> (a) (b) (c)</span> live in
            Settings &rarr; Total Recall.
          </p>
        </Section>

        {/* THIRD ROW — TEN OUTS */}
        <Section title="Third row · Ten Outs" delay={3}>
          <Effect>
            <p>
              <strong className="text-white/90">Premise:</strong> a
              prediction for an open-ended choice (ESP symbols, colours,
              numbers, names &hellip; any short list of up to ten).
            </p>
            <p>
              Open the third decoy row, mime typing a quick prediction,
              and hand the phone to the spectator with the screen facing{" "}
              <em>you</em> (their arm extended out). Ask them to settle
              on one of the items &mdash; they can say it out loud,
              whisper it, or even draw the shape in the air (this is not
              voice-dependent). You keep them talking until the cycling
              word in their note matches their choice, then quietly say{" "}
              <span className="text-white/85">&ldquo;turn it over&rdquo;</span>{" "}
              &mdash; the locked sentence reads as a perfect prediction.
            </p>
          </Effect>
          <Item icon={Hash} title="Cycling words, locked by a phone flip">
            Turn <strong className="text-white/80">Ten Outs</strong> on
            (Settings → Ten Outs) and set up to ten preset words plus a
            sentence template like
            <span className="text-white/85"> &quot;Your card is the (xxx).&quot;</span>
            The words cycle silently in the background at whatever
            interval you pick.
          </Item>
          <Item icon={Smartphone} title="Hand it screen-out, let them flip it">
            Hand the spectator the phone with the screen facing
            <em> you</em>. When they flip it toward themselves, the word
            showing at that instant locks into the prediction sentence
            in the third decoy row. Tap that row to read it back.
          </Item>
          <p className="text-[12.5px] text-white/55 mt-2">
            Tune the cycle interval in Settings &mdash; faster cycles make
            the &ldquo;lock&rdquo; feel more random but give you a tighter
            timing window.
          </p>
        </Section>

        {/* FLIP FORCE */}
        <Section title="Flip Force · Force any note by flipping" delay={3}>
          <Effect>
            <p>
              <strong className="text-white/90">Premise:</strong> the
              spectator <em>themselves</em> opens a random note from your
              folder &mdash; and somehow it&rsquo;s exactly the one that
              reveals their thought / card / chosen word. Works as a
              direct revelation or a mind-reading reveal.
            </p>
            <p>
              Show the decoy notes list and hand the phone over{" "}
              <strong className="text-white/85">face-down</strong>. Say:{" "}
              <span className="text-white/85">&ldquo;Let&rsquo;s test
              it once first.&rdquo;</span> While holding it face-down
              they scroll the list and tap somewhere at random. Now they
              turn the phone face-up <em>away</em> from you (you
              don&rsquo;t want to see). Ask:{" "}
              <span className="text-white/85">&ldquo;Did you open a
              note?&rdquo;</span> &mdash; <em>perfect</em>. That was the
              test. Go back and do it once more &ldquo;for real&rdquo;.
              The second flip lands on your forced note. Because the
              first run looked utterly innocent, the second feels just
              as random &mdash; you let them practise the dirty work.
            </p>
          </Effect>
          <Item icon={RotateCcw} title="Flip the phone face-down to swap notes">
            Turn <strong className="text-white/80">Flip Force</strong> on
            (Settings → Flip Force) and pick the note row you want to
            force. The decoy folder behaves exactly like Apple Notes
            until the phone goes face-down on the table &mdash; at that
            moment, the chosen row silently rewrites itself with your
            force content. When the spectator turns the phone back over
            and taps that row, your forced note is waiting.
          </Item>
          <Item icon={Repeat} title="Why two force notes? The convincer.">
            This is the secret weapon. In Settings you can configure a{" "}
            <strong className="text-white/85">second</strong> force note
            that triggers on the <em>next</em> flip. The play:
            <span className="block mt-2 pl-3 border-l border-[#d6c98c]/40 text-white/65">
              1. Let the spectator practice once &mdash; flip, peek,
              flip back. They see ordinary notes (or your first
              decoy/practice content). It feels harmless and random.
              <br />
              2. Now for &ldquo;the real one&rdquo;, they flip again
              &mdash; and <em>this time</em> the row contains the
              actual force you needed all along.
            </span>
            Because they&rsquo;ve already seen the notes change once
            without anything special happening, the second flip lands
            as innocent &mdash; no suspicion that the phone is doing
            the work. You let them practice the dirty work themselves.
          </Item>
          <Item icon={Hand} title="Strict order, hard reset">
            Sequence is fixed: <strong className="text-white/85">Force&nbsp;1 → Force&nbsp;2 → stop</strong>.
            After Force 2 fires, further flips do nothing. To start
            over for a new spectator, triple-tap the folder header (or
            the top strip) &mdash; this resets the Flip Force stage and
            sends you back to setup. Configure-and-forget; no on-screen
            tells.
          </Item>
          <Item icon={Smartphone} title="iOS motion permission, dwell threshold">
            iOS 13+ requires motion permission &mdash; the app prompts
            on first use. Set the <em>minimum face-down dwell</em> in
            Settings (default ~600&nbsp;ms) so accidental tilts on a
            handover don&rsquo;t misfire. Long enough that placing the
            phone flat triggers it; short enough that it feels instant.
          </Item>
          <p className="text-[12.5px] text-white/55 mt-2">
            Pair Flip Force with realistic per-row dates and bodies in
            Settings &rarr; Decoy notes so the &ldquo;before&rdquo;
            state is as boring as the rest of the folder. The
            convincer only works if the practice flip looks like
            nothing happened.
          </p>
        </Section>

        {/* PENCIL ICON — ADD-A-NUMBER */}
        <Section title="Pencil icon · Add-a-Number force" delay={3}>
          <Effect>
            <p>
              <strong className="text-white/90">Premise:</strong> three
              spectators each write a freely chosen multi-digit number;
              a fourth person adds them on their <em>own</em> calculator
              &mdash; the total is your prediction (e.g.{" "}
              <span className="text-white/85">11111111</span>).
            </p>
            <p>
              Open a note, type{" "}
              <span className="text-white/85">1.</span>{" "}
              <span className="text-white/85">2.</span>{" "}
              <span className="text-white/85">3.</span> and arm with the
              pencil icon. Spectator 1 types any random string of digits
              on line 1, hands the phone to spectator 2 for line 2, then
              to spectator 3 for line 3. While spectator 3 is finishing,
              you ask a fourth person to pull out their phone&rsquo;s
              calculator. Spectator 3 hands the phone to spectator 4
              &mdash; in that natural handoff motion the app silently
              rewrites one of the lines so the sum becomes your forced
              total. Spectator 4 adds the three numbers on their{" "}
              <em>own</em> device and reads out your prediction.
            </p>
          </Effect>
          <Item icon={Calculator} title="Three audience numbers, one preset total">
            Open any note and tap the
            <span className="text-white/85"> pencil icon</span> in the
            toolbar to silently arm the routine. Three spectators each
            write a number on lines
            <span className="text-white/85"> &quot;1.&quot;</span>,
            <span className="text-white/85"> &quot;2.&quot;</span>,
            <span className="text-white/85"> &quot;3.&quot;</span> The phone
            swaps one of those numbers behind the scenes so the total
            always matches your preset force.
          </Item>
          <Item icon={Hand} title="Pick which line gets swapped">
            In Settings → Add-a-Number choose which of the three lines
            the app overwrites (1, 2 or 3). Tucking the swap in the
            middle line reads most naturally for laymen.
          </Item>
          <Item icon={MoveUpRight} title="Fire it with a natural action">
            Pick the trigger in Settings: <em>keyboard dismiss</em>
            (Done / Return), <em>handoff motion</em> (passing the phone
            between hands) or <em>place-down</em> (setting it flat on the
            table). Motion options need iOS motion permission &mdash; the
            app auto-prompts the first time you arm with one selected.
          </Item>
        </Section>

        {/* LONG-PRESS COUNTER — APOCALYPSE */}
        <Section title="Long-press the notes counter · Apocalypse" delay={4}>
          <Effect>
            <p>
              <strong className="text-white/90">Premise:</strong> the
              spectator&rsquo;s thought-of card haunts your phone. The
              device glitches in their own hand and reveals it.
            </p>
            <p>
              Once you know their card (by any method), arm Apocalypse
              with the secret gesture and place the phone{" "}
              <strong className="text-white/85">face-up, flat in their
              palm</strong> on the fake iOS lockscreen. Tell them your
              passcode out loud &mdash; the digits secretly encode the
              card (first two = value, third = suit, the rest are
              wildcards). You can <em>call the last digit at random</em>{" "}
              so it sounds completely natural. The phone &ldquo;unlocks&rdquo;
              to your notes folder. Watch the tiny indicator; on the
              beat, snap your fingers &mdash; every note glitches,
              wipes, and resolves into your prediction of their card.
              They shake the phone to clean it. Everything happens in{" "}
              <em>their</em> hands.
            </p>
          </Effect>
          <Item icon={Lock} title="The signature effect">
            Long-press the &ldquo;X notes&rdquo; counter that sits under
            the folder title. A pixel-perfect fake iOS lockscreen takes
            over. The spectator (or you) enters a coded passcode, the
            phone &ldquo;unlocks&rdquo; back to the notes folder &mdash;
            and seconds later every preset note glitches, wipes and
            resolves into your forced sentence revealing the card they
            chose.
          </Item>
          <Item icon={KeyRound} title="The passcode encoding">
            First two digits = card value, third = suit, the rest are
            wildcards (type whatever feels natural).
          </Item>
          <div
            className="mt-2 p-3 rounded-lg text-[12px] leading-relaxed font-mono whitespace-pre-wrap"
            style={{ background: "rgba(0,0,0,0.32)", color: "rgba(214,201,140,0.85)" }}
          >
{`Value (first two digits)
  01 Ace   02 Two   03 Three  04 Four   05 Five
  06 Six   07 Seven 08 Eight  09 Nine   10 Ten
  11 Jack  12 Queen 13 King

Suit (third digit)
  1 ♠ Spades   2 ♥ Hearts   3 ♣ Clubs   4 ♦ Diamonds

Example
  0414 (4-digit mode) → Four of Spades (last digit wildcard)`}
          </div>
          <Item icon={Smartphone} title="Match the keypad to your real phone">
            Set the passcode length in Settings to whatever your real
            device uses (4 or 6 digits) so the keypad layout doesn&rsquo;t
            tell. Digits beyond the third are wildcards either way.
          </Item>
          <Item icon={CalendarClock} title="Trigger delay">
            The phone looks completely normal in the spectator&rsquo;s
            hand for the number of seconds you set before the glitch
            begins (0 = instant, max 100). Use the silence to deliver a
            line of patter.
          </Item>
          <Item icon={Hand} title="Reset by shaking">
            After the reveal lands the glitch keeps simmering until the
            spectator (or you) physically shakes the phone. One- and
            two-shake modes are available in Settings &mdash; two-shake
            re-glitches once into a second prediction before cleaning up.
          </Item>
        </Section>

        {/* + BUTTON — PHANTOM PEEK / COMPOSE */}
        <Section title="+ button · Phantom Peek (compose)" delay={4}>
          <Effect>
            <p>
              <strong className="text-white/90">Premise:</strong> a true
              one-word mind-read &mdash; you never see what they wrote,
              they delete it themselves, yet you still know.
            </p>
            <p>
              Open your decoy notes, tap the{" "}
              <span className="text-white/85">+</span> icon to create a
              fresh note, and hand the phone to the spectator. They type
              any single word (or short prompt), can show it to other
              people, but <strong className="text-white/85">not to
              you</strong>. Now ask them to back out of the note and{" "}
              <em>delete it</em> &mdash; just say{" "}
              <span className="text-white/85">&ldquo;hand me the phone
              now&rdquo;</span>, never &ldquo;close the app&rdquo;. The
              instant the device is back in your hand, the screen quietly
              shows you what they typed, and you immediately proceed to
              read their mind.
            </p>
            <p className="text-white/55 text-[12.5px]">
              The multi-question variant below stacks the same peek with
              up to three prompts in one go &mdash; great for combining a
              calculation, a date, and a free-form question.
            </p>
          </Effect>
          <Item icon={ListOrdered} title="Up to three questions in one go">
            The + button opens a fresh note with multi-question parsing
            always on. Spectator prefixes their prompts with
            <span className="text-white/85"> 1:</span>,
            <span className="text-white/85"> 2:</span>,
            <span className="text-white/85"> 3:</span> on separate lines.
            A single peek returns every answer joined by
            <span className="text-white/85"> &quot; | &quot;</span>.
          </Item>
          <pre
            className="mt-2 p-3 rounded-lg text-[12px] leading-relaxed font-mono whitespace-pre-wrap"
            style={{ background: "rgba(0,0,0,0.32)", color: "rgba(214,201,140,0.85)" }}
          >
{`1:
12345 + 67890

2:
28 april 1999

3:
capital of france`}
          </pre>
          <p className="mt-2 text-[12.5px] text-white/55">
            Yields:{" "}
            <span className="text-white/85">80235 | Wednesday, 28 April 1999 · Taurus | Paris</span>
          </p>
          <Item icon={Pencil} title="Draw or Type — your choice">
            Pick the compose input in <strong className="text-white/80">Settings → Compose input</strong>.
            Draw uses GPT vision; Type uses pure text. Both run the same
            reveal gestures.
          </Item>
        </Section>

        {/* REVEAL STYLES */}
        <Section title="Reveal styles (compose notes only)" delay={4}>
          <p className="text-[13px] text-white/55 mb-4 -mt-1">
            Pick one in <strong className="text-white/80">Settings → Reveal</strong>.
          </p>
          <Item icon={Eye} title="Secret peek — subliminal in the date">
            The answer is rendered <em>inside the timestamp row</em> in the
            same muted grey (e.g. <span className="text-white/85">June 8, 2026 09:14 · 10000</span>).
            The screen does not change. The spectator sees nothing. You
            glance at the date and read.
          </Item>
          <Item icon={ArrowDownToLine} title="Scroll peek">
            The answer is invisibly appended dozens of empty lines below
            the fold. Hand the spectator the device — they read the top of
            the note. Quietly scroll down later and your answer is waiting.
          </Item>
          <Item icon={Headphones} title="Whisper">
            The answer is spoken through your earbud the instant the peek
            lands. Your eyes never leave theirs.
          </Item>
          <Item icon={Minimize2} title="Compact reveal — never wraps">
            Optional add-on (Settings → Compact reveal). Weekdays,
            months and zodiac signs are abbreviated to two-letter codes
            (<span className="text-white/85">Mo Tu We</span>,{" "}
            <span className="text-white/85">Ja Fe Ma</span>,{" "}
            <span className="text-white/85">Ar Ta Ge</span>) so the full
            subliminal answer fits on a single line of any phone.
          </Item>
        </Section>

        {/* GESTURES */}
        <Section title="Secret gestures (trick screen)" delay={4}>
          <Item icon={Hand} title="Tap the three dots">
            Fires the per-mode compute and shows the answer subliminally
            in the date line (or appends/whispers in the matching reveal
            style). Tap again to hide it and bring back the date.
          </Item>
          <Item icon={Hand} title="Long-press the date">
            <strong className="text-white/85">Silent redo</strong> — re-runs the latest
            computation in case the spectator changed their input or you
            misheard. The new value quietly replaces the old one. No
            flicker. No overlay.
          </Item>
          <Item icon={Hand} title="Triple-tap the date">
            Exits the trick and returns to the decoy folder. Title, body,
            drawing and system clipboard are wiped so the next performance
            starts clean.
          </Item>
          <Item icon={ArrowLeft} title="Tap the yellow ‘Notes’ back arrow">
            Same as triple-tap — exits and resets. Use whichever feels
            more natural for the moment.
          </Item>
          <p className="text-[12.5px] text-white/55 mt-2">
            The Swiper note is static — the answer is the body, so the
            date-line gestures don&rsquo;t apply there.
          </p>
        </Section>

        {/* DATES & BACKDATE */}
        <Section title="Timestamps & realism" delay={4}>
          <Item icon={CalendarClock} title="Set any date+time per decoy row">
            Each row in <strong className="text-white/80">Settings → Decoy notes</strong> has
            its own date+time picker. Empty = auto labels
            (Today / Yesterday / Friday / 14 Apr 2024). Set a value and
            the row uses it verbatim — perfect for making the Swiper note
            look like it was written last Tuesday.
          </Item>
          <Item icon={CalendarClock} title="Or rely on Backdate">
            Leave the per-row times empty and turn on
            <strong className="text-white/80"> Backdate</strong>. The
            Swiper row shifts into the past by the number of
            minutes you set.
          </Item>
          <Item icon={CalendarClock} title="Birthdays, weekdays, zodiacs">
            Whenever a compose prompt is a date the app returns
            <span className="text-white/85"> Weekday, D Month YYYY · Zodiac</span>.
            Example: <span className="text-white/85">&quot;28 april 1999&quot;</span>
            {" → "}
            <span className="text-white/85">&quot;Wednesday, 28 April 1999 · Taurus&quot;</span>.
          </Item>
        </Section>

        {/* RITUAL */}
        <Section title="Ritual & tips" delay={4}>
          <ul className="space-y-3 text-[14px] text-white/75 leading-relaxed list-disc pl-5 marker:text-[#d6c98c]">
            <li>
              Set a <strong className="text-white/85">default note title</strong>{" "}
              (or one per decoy row) in Settings so every fresh trick opens
              with a believable headline that fits your patter.
            </li>
            <li>
              Curate the <strong className="text-white/85">decoy notes</strong> in Settings so the
              folder looks like a real, lived-in life. Realism is the
              cover — boring is good.
            </li>
            <li>
              Pick a <strong className="text-white/85">language</strong> for Whisper. Auto-detect
              works, but locking it eliminates a half-second of guessing.
            </li>
            <li>
              Rehearse the <em>silent redo</em> long-press until you can do
              it while smiling at the spectator.
            </li>
            <li>
              When in doubt, use the yellow back arrow to wipe everything
              and start fresh. The clipboard is cleared automatically.
            </li>
          </ul>
        </Section>

        {/* CTA */}
        <div className="mt-10 grid grid-cols-2 gap-3 mr-rise delay-4">
          <Button
            onClick={() => navigate("/settings")}
            className="h-12 rounded-2xl text-[#f0ead4] mr-cta"
            style={{
              background: "linear-gradient(180deg, #a11421 0%, #6c0c16 100%)",
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              fontSize: "12px",
            }}
            data-testid="tutorial-to-settings"
          >
            Configure
          </Button>
          <Button
            onClick={() => navigate("/notes")}
            className="h-12 rounded-2xl text-[#f0ead4] mr-cta mr-cta-breath"
            style={{
              background: "linear-gradient(180deg, #a11421 0%, #6c0c16 100%)",
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              fontSize: "12px",
            }}
            data-testid="tutorial-to-notes"
          >
            Open Notes
          </Button>
        </div>

        <footer className="mt-12 flex flex-col items-center text-center gap-2">
          <div className="mr-rule" style={{ width: "2.5rem" }} />
          <p
            className="text-[10px] tracking-[0.42em] uppercase"
            style={{ color: "rgba(214,201,140,0.55)" }}
          >
            Conceived by Dennis Reinsma
          </p>
        </footer>
      </div>
    </div>
  );
}

function Section({ title, delay = 2, children }) {
  return (
    <Card
      className={`border-white/10 bg-white/[0.04] backdrop-blur p-5 sm:p-6 rounded-2xl mb-5 mr-rise delay-${delay}`}
    >
      <div className="flex items-center gap-3 mb-4">
        <div
          className="h-px flex-1"
          style={{ background: "linear-gradient(90deg, transparent, rgba(214,201,140,0.4), transparent)" }}
        />
        <h2 className="text-[11px] tracking-[0.42em] uppercase" style={{ color: "rgba(214,201,140,0.85)" }}>
          {title}
        </h2>
        <div
          className="h-px flex-1"
          style={{ background: "linear-gradient(90deg, transparent, rgba(214,201,140,0.4), transparent)" }}
        />
      </div>
      <div className="space-y-4">{children}</div>
    </Card>
  );
}

function Item({ icon: Icon, title, children }) {
  return (
    <div className="flex gap-3">
      <div
        className="shrink-0 w-8 h-8 rounded-full flex items-center justify-center"
        style={{ background: "rgba(214,201,140,0.08)", border: "1px solid rgba(214,201,140,0.18)" }}
      >
        <Icon className="w-4 h-4" style={{ color: "#d6c98c" }} />
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="text-[14px] text-white font-semibold mb-1">{title}</h3>
        <p className="text-[13.5px] text-white/70 leading-relaxed">{children}</p>
      </div>
    </div>
  );
}

function Effect({ children }) {
  return (
    <div
      className="rounded-xl px-4 py-3 mb-1"
      style={{
        background:
          "linear-gradient(180deg, rgba(214,201,140,0.07) 0%, rgba(214,201,140,0.03) 100%)",
        border: "1px solid rgba(214,201,140,0.22)",
      }}
    >
      <div
        className="text-[10px] tracking-[0.36em] uppercase mb-2"
        style={{ color: "rgba(214,201,140,0.85)" }}
      >
        The Effect
      </div>
      <div className="text-[13px] text-white/80 leading-relaxed space-y-2">
        {children}
      </div>
    </div>
  );
}
